import React, { FormEvent } from 'react';
import PropTypes from 'prop-types';

import { Window } from '../utils/interfaces';

import styles from './styles.module.css';

interface Props {
  grecaptchaApiKey: string;
  setGrecaptchaId: (id: string) => void;
  elementId: string;
  size: string;
  onCaptchaResponse: (token: string) => void;
  hiddenOnMobile: boolean;
}

class Grecaptcha extends React.Component<Props> {
  grecaptchaId: string | null;

  constructor(props: Props) {
    super(props);
    this.grecaptchaId = null;

    this._onSubmit = this._onSubmit.bind(this);
    this._onCaptchaResponse = this._onCaptchaResponse.bind(this);
  }

  override componentDidMount(): void {
    const { grecaptchaApiKey, setGrecaptchaId, elementId, size } = this.props;

    this._addScript();

    const initializeGrecaptcha = (): void => {
      // grecaptcha.ready allows us to avoid a race condition with the loading of the grecaptcha api
      (window as Window).grecaptcha?.ready(() => {
        this.grecaptchaId = (window as Window).grecaptcha?.render(elementId, {
          sitekey: grecaptchaApiKey,
          size,
          callback: () => this._onCaptchaResponse(),
        }) as string;

        setGrecaptchaId(this.grecaptchaId);
      });
    };

    const waitForGrecaptcha = (): void => {
      if (
        (window as Window).grecaptcha &&
        typeof (window as Window).grecaptcha?.ready === 'function'
      ) {
        initializeGrecaptcha();

        return;
      }

      setTimeout(() => {
        waitForGrecaptcha();
      }, 100);
    };

    waitForGrecaptcha();
  }

  _addScript(): void {
    const src = 'https://www.google.com/recaptcha/api.js?render=explicit';

    if (document.querySelectorAll(`[src="${src}"]`).length > 0) {
      return;
    }

    const grecaptchaScript = document.createElement('script');

    grecaptchaScript.src = src;

    grecaptchaScript.async = true;
    grecaptchaScript.defer = true;

    document.head.appendChild(grecaptchaScript);
  }

  _onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();

    (window as Window).grecaptcha?.execute(this.grecaptchaId as string);
  }

  _onCaptchaResponse(): void {
    const grecaptchaToken = (
      window as Window
    ).grecaptcha?.getResponse() as string;

    this.props.onCaptchaResponse(grecaptchaToken);

    (window as Window).grecaptcha?.reset(this.grecaptchaId as string);
  }

  override render(): JSX.Element {
    const { hiddenOnMobile, elementId } = this.props;

    return (
      <div
        id={elementId}
        className={hiddenOnMobile ? styles.hiddenOnMobile : ''}
      />
    );
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
Grecaptcha.propTypes = {
  onCaptchaResponse: PropTypes.func.isRequired,
  grecaptchaApiKey: PropTypes.string.isRequired,
  hiddenOnMobile: PropTypes.bool,
  elementId: PropTypes.string.isRequired,
  setGrecaptchaId: PropTypes.func,
  size: PropTypes.string.isRequired,
};

export default Grecaptcha;
